import { useUserManagementStore } from "@hooks/stores";
import { Stack } from "@ucchub/ui";
import Button from "@vfuk/core-button";
import Spacing from "@vfuk/core-spacing";
import { useNavigate } from "react-router-dom";
import { User } from "src/interfaces/user";
import DeactivateUserModal from "../DeactivateUserModal/DeactivateUserModal";
import ReactivateUserModal from "../ReactivateUserModal/ReactivateUserModal";
import DeletesUserModal from "../DeleteUserModal/DeletesUserModal";
import { UserTypes } from "@hub/common-libs";
import { useState } from "react";
import { UserStatus } from "../UserManagement.types";
import { useTranslation } from "react-i18next";

type ButtonProps = {
  isLoading: boolean;
  user: User;
  currentUserId: string;
  userId: string;
  isActiveUser: boolean;
  userActions: UserTypes.actionsResponse;
  isUserDeactivate: boolean;
  notOrganisationStatus: boolean;
};

const ViewUserButtons = ({
  isLoading,
  user,
  currentUserId,
  userId,
  isActiveUser,
  userActions,
  isUserDeactivate,
  notOrganisationStatus,
}: ButtonProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation("translation", {
    keyPrefix: "shared:users",
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);

  const selectedUsers = useUserManagementStore((state) => state.selectedUsers);
  const addSelectedUser = useUserManagementStore(
    (state) => state.addSelectedUser
  );
  const redirectToEdit = (userId: string) => {
    navigate("/users/" + encodeURIComponent(userId) + "/edit");
  };

  const { call: fetchUser } = useUserManagementStore(
    (state) => state.fetchUser
  );
  const handleDeactivateClicked = () => {
    // The user gets de-selected when navigating back from the Edit User page.
    // we want to "select" the user again so that the messaging is correct in the modal.
    if (selectedUsers.length === 0) {
      addSelectedUser(user);
    }
    setShowDeactivateModal(true);
  };

  const handleDeleteClicked = () => {
    // The user gets delete when navigating back from the Edit User page.
    // we want to "select" the user again so that the messaging is correct in the modal.
    if (selectedUsers.length === 0) {
      addSelectedUser(user);
    }
    setShowDeleteModal(true);
  };

  const handleReactivateClicked = () => {
    // The user gets de-selected when navigating back from the Edit User page.
    // we want to "select" the user again so that the messaging is correct in the modal.
    if (selectedUsers.length === 0) {
      addSelectedUser(user);
    }
    setShowReactivateModal(true);
  };

  const isUserDelete = !isLoading && currentUserId === userId && isActiveUser;

  const deactivateSelectedUser = useUserManagementStore(
    (state) => state.deactivateSelectedUser
  );
  const deleteSelectedUser = useUserManagementStore(
    (state) => state.deleteSelectedUser
  );
  const clearUsers = useUserManagementStore((state) => state.clearUsers);

  const clearAllSelectedUsers = useUserManagementStore(
    (state) => state.clearAllSelectedUsers
  );

  return (
    <div>
      <Spacing spacingLevel={{ top: 4, bottom: 4 }}>
        <Stack justifyContent="right">
          <Stack>
            {!isLoading &&
              userActions.delete &&
              user.status !== UserStatus.DELETED && (
                <Spacing spacingLevel={{ right: 2 }}>
                  <Button
                    onClick={handleDeleteClicked}
                    text={t("btn_delete_user")}
                    srText="Delete user"
                    appearance="alt1"
                    state={isUserDelete ? "disabled" : undefined}
                  />
                </Spacing>
              )}
            <Spacing spacingLevel={{ right: 2 }}>
              {!isLoading &&
                userActions.delete &&
                user.status !== UserStatus.DELETED && (
                  <>
                    {isActiveUser ? (
                      <Button
                        onClick={handleDeactivateClicked}
                        text={t("btn_deactivate_user")}
                        srText="Deactivate user"
                        appearance="alt1"
                        state={isUserDeactivate ? "disabled" : undefined}
                      />
                    ) : (
                      <Button
                        onClick={handleReactivateClicked}
                        text={t("btn_reactivate_user")}
                        srText="Re-activate user"
                        appearance="primary"
                        state={
                          isUserDeactivate || notOrganisationStatus
                            ? "disabled"
                            : undefined
                        }
                      />
                    )}
                  </>
                )}
            </Spacing>
            {!isLoading && userActions.edit && (
              <Button
                onClick={() => {
                  user.userId && redirectToEdit(user.userId);
                  clearAllSelectedUsers();
                }}
                state={!isActiveUser ? "disabled" : undefined}
                text={t("btn_edit_user")}
                srText={userActions.edit.altText}
                appearance={!isActiveUser ? "alt1" : "primary"}
              />
            )}
          </Stack>
        </Stack>
      </Spacing>

      {!isLoading && userActions.delete && showDeleteModal && (
        <DeletesUserModal
          show={showDeleteModal}
          userId={userId}
          user={user}
          onClose={() => {
            setShowDeleteModal(false);
            deleteSelectedUser();
            fetchUser(userId);
          }}
          reloadList={() => {
            clearUsers();
            deleteSelectedUser();
          }}
        />
      )}
      {!isLoading && userActions.delete && (
        <>
          {isActiveUser ? (
            <DeactivateUserModal
              show={showDeactivateModal}
              userId={userId}
              user={user}
              onClose={() => {
                setShowDeactivateModal(false);
                deactivateSelectedUser();
                fetchUser(userId);
                setShowDeleteModal(false);
              }}
              reloadList={() => {
                clearUsers();
                deactivateSelectedUser();
              }}
            />
          ) : (
            <ReactivateUserModal
              show={showReactivateModal}
              userId={userId}
              user={user}
              onClose={() => {
                setShowReactivateModal(false);
                deactivateSelectedUser();
                fetchUser(userId);
                setShowDeleteModal(false);
              }}
              reloadList={() => {
                clearUsers();
                deactivateSelectedUser();
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ViewUserButtons;
